import { browserSupportsWebAuthn, startAuthentication, startRegistration } from "@simplewebauthn/browser";
import { api } from "./api";
import type { PublicLanguage } from "./i18n";

const setupReminderKey = "study-space-passkey-setup-reminder";

export function passkeysSupported() {
  return typeof window !== "undefined" && browserSupportsWebAuthn();
}

export async function authenticateWithPasskey(email: string, language: PublicLanguage) {
  if (!passkeysSupported()) return { available: false as const };
  const beginning = await api.passkeyAuthenticationOptions(email, language);
  if (!beginning.available) return beginning;
  const credential = await startAuthentication({ optionsJSON: beginning.options });
  const result = await api.verifyPasskeyAuthentication(beginning.challengeId, credential, language);
  return { available: true as const, user: result.user };
}

export const passkeyErrorCopy: Record<PublicLanguage, { unsupported: string; cancelled: string; unavailable: string }> = {
  en: { unsupported: "Passkeys are not supported by this browser.", cancelled: "The passkey prompt was closed or timed out. You can try again or use an email code.", unavailable: "The passkey could not be used. Try again or use an email code." },
  zh: { unsupported: "此浏览器不支持通行密钥。", cancelled: "通行密钥提示已关闭或超时。您可以重试或使用电子邮件验证码。", unavailable: "无法使用通行密钥。请重试或使用电子邮件验证码。" },
  th: { unsupported: "เบราว์เซอร์นี้ไม่รองรับพาสคีย์", cancelled: "หน้าต่างพาสคีย์ถูกปิดหรือหมดเวลา คุณสามารถลองอีกครั้งหรือใช้รหัสทางอีเมล", unavailable: "ไม่สามารถใช้พาสคีย์ได้ โปรดลองอีกครั้งหรือใช้รหัสทางอีเมล" },
  my: { unsupported: "ဤဘရောက်ဇာတွင် passkey မထောက်ပံ့ပါ။", cancelled: "Passkey အချက်ပေးဝင်းဒိုးကို ပိတ်လိုက်သည် သို့မဟုတ် အချိန်ကုန်သွားသည်။ ထပ်စမ်းနိုင်သလို အီးမေးလ်ကုဒ်ကိုလည်း သုံးနိုင်သည်။", unavailable: "Passkey ကို အသုံးမပြုနိုင်ပါ။ ထပ်စမ်းပါ သို့မဟုတ် အီးမေးလ်ကုဒ်ကို သုံးပါ။" },
  vi: { unsupported: "Trình duyệt này không hỗ trợ khóa truy cập.", cancelled: "Lời nhắc khóa truy cập đã bị đóng hoặc hết thời gian. Bạn có thể thử lại hoặc dùng mã qua email.", unavailable: "Không thể dùng khóa truy cập. Hãy thử lại hoặc dùng mã qua email." },
  es: { unsupported: "Este navegador no admite llaves de acceso.", cancelled: "La solicitud de la llave de acceso se cerró o agotó el tiempo. Puedes volver a intentarlo o usar un código por correo.", unavailable: "No se pudo usar la llave de acceso. Inténtalo de nuevo o usa un código por correo." },
};

export async function registerPasskey(name: string, language: PublicLanguage = "en") {
  if (!passkeysSupported()) throw new Error(passkeyErrorCopy[language].unsupported);
  const beginning = await api.passkeyRegistrationOptions(name, language);
  const credential = await startRegistration({ optionsJSON: beginning.options });
  return api.verifyPasskeyRegistration(beginning.challengeId, credential, language);
}

export function passkeyFailureMessage(error: unknown, language: PublicLanguage = "en") {
  if (error instanceof Error && error.name === "NotAllowedError") return passkeyErrorCopy[language].cancelled;
  return error instanceof Error ? error.message : passkeyErrorCopy[language].unavailable;
}

export function rememberPasskeySetupOffer() {
  if (typeof window !== "undefined") window.localStorage.setItem(setupReminderKey, "1");
}

export function passkeySetupOfferPending() {
  return typeof window !== "undefined" && window.localStorage.getItem(setupReminderKey) === "1";
}

export function dismissPasskeySetupOffer() {
  if (typeof window !== "undefined") window.localStorage.removeItem(setupReminderKey);
}
